import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, Button, Alert, Grid, TextField, IconButton, Tooltip, InputAdornment, useMediaQuery, useTheme, Stack, Menu, MenuItem, ListItemIcon, ListItemText, } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useSnackbar } from '../../stateless/SnackBarProvider';
import { GridRowId } from '@mui/x-data-grid';
import CommonDataGrid from '../../stateless/CommonDataGrid';
import ErrorHandler from '../../stateless/Common/ErrorHandler';
import { useBlockUnblockItemCategory, useDeleteItemCategory, useGetItemCategories } from '../../Query/ItemCategoryQuery';
import ActionButtons from '../../stateless/Common/ActionButtons';
import { useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { categoryStyles } from '../../Styles/CategoryFormStyles';
import Accordian from '../../stateless/Accordian';
import { useForceNavigate } from '../../HelperFunction/Hooks/forceNavigateWithQuery';
import { useChoiceParams } from '../ManageChoice/useChoiceParams';
import { useDebounce } from '../../../utils/useDounce';
import { useTranslation } from 'react-i18next';

export const itemSubCategoryAtom = atomWithStorage<any>(
  'itemSubCategoryId',
  null
);

interface ItemCategory {
  id: string;
  nameEnglish: string;
  nameArabic: string;
  status: 'blocked' | 'unblocked' | string;
  addedBy: string;
  [key: string]: any;
}


interface ItemCategoryListProps {
  editCategory: (category: ItemCategory) => void;
  mode: 'add' | 'edit';
  modal: boolean;
  setModal: React.Dispatch<React.SetStateAction<boolean>>;
  setMode: React.Dispatch<React.SetStateAction<'add' | 'edit'>>;
  storeId?: string;
}

const ItemCategoryList: React.FC<ItemCategoryListProps> = ({ editCategory, mode, setMode }) => {
  const { t, i18n } = useTranslation();
  const [, forceUpdate] = useState({});

  const setitemSubCategory = useSetAtom(itemSubCategoryAtom);
  const { showMessage } = useSnackbar();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const [open, setOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<ItemCategory | null>(null);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage] = useState<string>('');
  const [snackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('info');

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);


  const { params, dispatch } = useChoiceParams();
  const isReady = Boolean(params && Object.keys(params).length > 0);

  const memoizedParams = useMemo(() => {
    if (!isReady) return {};
    const { storeId, ...rest } = params;
    void storeId;
    return rest;
  }, [isReady, params]);
  const { data: getItemCategory, isLoading, refetch } = useGetItemCategories(
    memoizedParams as Parameters<typeof useGetItemCategories>[0]
  );


  const { forceNavigateWithQuery } = useForceNavigate();

  const { mutate: deleteItemCategoryById } = useDeleteItemCategory();
  const { mutate: blockUnblockItemCategoryById } =
    useBlockUnblockItemCategory();

  const filteredRows = React.useMemo(() => {
    if (!getItemCategory?.data) return [];
    const searchLower = params.search?.toLowerCase().trim() || '';
    const storeOwners = getItemCategory?.data || []

    const filtered = storeOwners
      .filter((row) => {

        if (!row) return false;
        const name = (row.nameEnglish?.toLowerCase() || '').toLowerCase();
        return (
          name.includes(searchLower)
        );
      })


    return filtered.map((row, index) => ({
      ...row,
      id: row.id,
      sno: index + 1 + (Number(params.page - 1)) * Number(params.limit),
      nameEnglish: row.nameEnglish ?? 'N/A',
      nameArabic: row.nameArabic ?? 'N/A',
      status: row.status || 'unblocked',
      addedBy: row.addedBy || '',
      storeId: row.storeId || '',
      storeType: row.storeType || '',
      categoryName: i18n.language === 'ar' ? row.nameArabic : row.nameEnglish,
    }));
  }, [params.search, getItemCategory, params.page, params.limit, i18n.language]);


  const handleEditCategory = (category: ItemCategory) => {
    setSelectedCategory(category);
    editCategory(category);
    setMode('edit');

    setOpen(true);
  };

  const handleDeleteClick = useCallback(() => {
    // if (selectedCategoryIds.length === 0) {
    //   setSnackbarMessage('Select at least one category to delete');
    //   setSnackbarSeverity('error');
    //   setSnackbarOpen(true);
    // } 
    if (selectedCategoryIds.length === 0) {
      showMessage(t('itemCategory.messages.selectOne'), 'error');
      return;
    }
    else {
      const confirmed = window.confirm(
        t('itemCategory.messages.confirmDelete')
      );
      if (!confirmed) return;

      deleteItemCategoryById(selectedCategoryIds, {
        onSuccess: () => {
          showMessage(t('itemCategory.messages.deletedSuccessfully'), 'success');
          setSelectedCategoryIds([]);
        },
        onError: () => showMessage(t('itemCategory.messages.errorDeleting'), 'error'),
      });
    }
  }, [selectedCategoryIds, deleteItemCategoryById, t]);

  const handleDelete = useCallback(
    (id: string) => {
      const confirmed = window.confirm(
        t('itemCategory.messages.confirmDeleteSingle')
      );
      if (!confirmed) return;

      deleteItemCategoryById([id], {
        onSuccess: () => {
          showMessage(t('itemCategory.messages.categoryDeletedSuccessfully'), 'success');
          setSelectedCategoryIds([]);
        },
        onError: () => showMessage(t('itemCategory.messages.errorDeletingCategory'), 'error'),
      });
    },
    [deleteItemCategoryById, t]
  );

  const handleBlock = () => {
    if (selectedCategoryIds.length === 0) {
      showMessage(t('itemCategory.messages.noCategorySelected', { action: 'block' }), 'error');
      return;
    }

    const confirmed = window.confirm(t('itemCategory.messages.confirmBlock'));
    if (!confirmed) return;

    blockUnblockItemCategoryById(
      {
        id: selectedCategoryIds,
        data: { status: 'blocked' },
      },
      {
        onSuccess: () => {
          refetch()
        },
      }
    );
  };

  const handleUnblock = () => {
    if (selectedCategoryIds.length === 0) {
      showMessage(t('itemCategory.messages.noCategorySelected', { action: 'unblock' }), 'error');
      return;
    }

    const confirmed = window.confirm(t('itemCategory.messages.confirmUnblock'));
    if (!confirmed) return;

    blockUnblockItemCategoryById(
      {
        id: selectedCategoryIds,
        data: { status: 'unblocked' },
      },
      {
        onSuccess: () => {
          refetch()
        },
      }
    );
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  };

  useEffect(() => {
    dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
  }, [debouncedSearch]);

  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };

  // const handleFilterChange = (sortBy: string, sortOrder: 'asc' | 'desc') => {
  //   dispatch({ type: 'SET_SORT', payload: { sortBy, sortOrder } });
  // };



  const totalItems = params.search || params.statusFilter !== 'All' || params.addedByFilter !== 'All'
    ? filteredRows.length
    : getItemCategory?.total || 0;



  const columns = useMemo(() => [
    {
      field: 'sno',
      headerName: t('itemCategory.columns.sno'),
      width: 90,
      disableColumnMenu: true,
      sortable: true,
    },
    { field: 'categoryName', headerName: t('itemCategory.columns.categoryName'), flex: 1 },
    {
      field: 'manageSubCategory',
      headerName: t('itemCategory.columns.manageSubCategory'),
      flex: 1,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: any) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => {
            console.log('params.row:', params.row);
            setitemSubCategory(params.row);
            forceNavigateWithQuery(`/layout/manageItemSubCategory/${params.row.id}`);

          }}
          sx={{ textTransform: 'none' }}
        >
          {t('itemCategory.actions.manageSubCategory')}
        </Button>
      ),
    },
    {
      field: 'addedBy',
      headerName: t('itemCategory.columns.addedBy'),
      width: 120,
      disableColumnMenu: true,
    },
    
    {
      field: 'status',
      headerName: t('itemCategory.columns.status'),
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: any) => {
        const isBlocked = params.row.status !== 'unblocked';
        const toggleStatus = async () => {
          const newStatus = isBlocked ? 'unblocked' : 'blocked';
          const confirmed = window.confirm(
            t('itemCategory.messages.confirmStatusChange', { status: newStatus })
          );
          if (!confirmed) return;

          try {
            await blockUnblockItemCategoryById({
              id: [params.row.id],
              data: { status: newStatus },
            });
            showMessage(t('itemCategory.messages.statusUpdatedSuccessfully', { status: newStatus }), 'success');
          } catch {
            showMessage(t('itemCategory.messages.failedUpdateStatus'), 'error');
          }
        };

        return (
          <Tooltip title={isBlocked ? t('itemCategory.messages.blocked') : t('itemCategory.messages.unblocked')}>
            <IconButton size="small" onClick={toggleStatus}>
              {isBlocked ? (
                <BlockIcon color="error" fontSize="small" />
              ) : (
                <CheckCircleIcon color="success" fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: t('itemCategory.columns.actions'),
      width: 100,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: any) => {
        const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
        const open = Boolean(anchorEl);
        return (
          <>
            <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
              <MenuItem onClick={() => { setAnchorEl(null); handleEditCategory(params.row); }}>
                <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                <ListItemText>{t('itemCategory.actions.edit')}</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { setAnchorEl(null); handleDelete(params.row.id); }}>
                <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
                <ListItemText sx={{ color: 'error.main' }}>{t('itemCategory.actions.delete')}</ListItemText>
              </MenuItem>
            </Menu>
          </>
        );
      },
    },
  ], [
    t,
    setitemSubCategory,
    forceNavigateWithQuery,
    handleEditCategory,
    handleDelete,
    blockUnblockItemCategoryById,
    showMessage
  ]);


  const handleCategorySelection = (selectionModel: GridRowId[]) => {
    const ids = selectionModel.map((id) => String(id));
    setSelectedCategoryIds(ids);
  };

  return (
    <Box sx={categoryStyles.ListContainer}>
      {snackbarOpen && (
        <Alert
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
          sx={{ mb: 2 }}
        >
          {snackbarMessage}
        </Alert>
      )}

      <Grid
        container
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        wrap="wrap"
        sx={{ mb: 2 }}
      >
        {/* Left-aligned Search */}
        <Box
          sx={{
            width: 600,
            border: '1px solid #ccc',
            borderRadius: '8px',
            background: '#fff',
          }}
        >
          <TextField
            fullWidth
            size="medium"
            placeholder={t('itemCategory.searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{
              '.MuiOutlinedInput-root': {
                height: 40,
                width: 600,
                border: 'none', // remove inner border
              },
              '& fieldset': {
                border: 'none', // remove Material-UI default border
              },
              '& .MuiInputBase-input': {
                paddingTop: '10px', // adjust this value to move the placeholder lower
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: '#aaa', mt: '4px' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Right-aligned Action Buttons + Accordion */}
        <Grid container justifyContent="flex-end" spacing={1} alignItems="center">
          <Grid>
            <Stack direction={isSmallScreen ? 'column' : 'row'} spacing={1}>
              <ActionButtons
                onBlock={handleBlock}
                onUnblock={handleUnblock}
                onDelete={handleDeleteClick}
                showBlock={selectedCategoryIds.length > 0 && filteredRows.some(r => selectedCategoryIds.includes(r.id) && r.status === 'unblocked')}
                showUnblock={selectedCategoryIds.length > 0 && filteredRows.some(r => selectedCategoryIds.includes(r.id) && r.status === 'blocked')}
                showDelete={selectedCategoryIds.length > 0}
              />
            </Stack>
          </Grid>
          <Grid>
            <Accordian
              handleAddCategory={() => {
                setOpen(true);
                setMode('add');
              }}
              open={open}
              setOpen={setOpen}
              mode={mode}
              initialData={
                selectedCategory
                  ? {
                    id: selectedCategory.id || '',
                    englishName: selectedCategory.nameEnglish || '',
                    arabicName: selectedCategory.nameArabic || '',
                    categoryImage: '',
                    categoryIcon: '',
                    addedBy: selectedCategory.addedBy || '',
                    storeId: selectedCategory.storeId || '',
                    storeType: selectedCategory.storeType || '',
                  }
                  : null
              }
              buttonMode="itemCategory"
            />
          </Grid>
        </Grid>
      </Grid>

      <Box width="100%" sx={{ overflowX: 'auto' }}>
        <CommonDataGrid
          key={`datagrid-page-${params.page}-limit-${params.limit}`}
          rows={filteredRows || []}
          columns={columns}
          pageSize={params.limit}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          checkboxSelection
          loading={isLoading}
          page={params.page - 1}
          rowCount={totalItems}
          components={{
            NoRowsOverlay: () => <ErrorHandler type="no-data" message={t('itemCategory.messages.noData')} onRetry={() => refetch()} />
          }}
          onRowSelectionModelChange={handleCategorySelection}
        />
      </Box>
    </Box>
  );
};

export default ItemCategoryList;
